import { FC, useState, useEffect, useCallback, useRef } from 'react'
import { Tree, NodeApi } from 'react-arborist'
import { jingqueAdminApi, CategoryForest, CategoryTreeNode, UpdateCategoryTypeRequest } from '@/api/jingqueAdminApi'
import { CategoryMindMap } from '@/components/admin/CategoryMindMap'

type TreeNodeData = { id: string; name: string; nodeType: 'folder' | 'leaf'; enabled: boolean; sortOrder: number; parentId: string | null; categoryType?: { id: string; name: string; description: string; extractionPrompt: string; enabled: boolean } }

function convertToTree(nodes: CategoryTreeNode[]): TreeNodeData[] {
  return nodes.map(n => ({
    id: n.id,
    name: n.name,
    nodeType: n.nodeType,
    enabled: n.enabled,
    sortOrder: n.sortOrder,
    parentId: n.parentId,
    categoryType: n.categoryType ? { id: n.categoryType.id, name: n.categoryType.name, description: n.categoryType.description, extractionPrompt: n.categoryType.extractionPrompt, enabled: n.categoryType.enabled } : undefined,
    children: n.children ? convertToTree(n.children) : undefined,
  })) as any
}

export const CategoryTreePage: FC = () => {
  const [forests, setForests] = useState<CategoryForest[]>([])
  const [selectedForestId, setSelectedForestId] = useState<string>('')
  const [treeData, setTreeData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [viewMode, setViewMode] = useState<'tree' | 'mindmap'>('tree')
  const [showCreateForest, setShowCreateForest] = useState(false)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; node: NodeApi<any> } | null>(null)
  const [showCreateNode, setShowCreateNode] = useState<{ parentId: string | null; type: 'folder' | 'leaf' } | null>(null)
  const [editingNode, setEditingNode] = useState<{ id: string; name: string; enabled: boolean } | null>(null)
  const [editingType, setEditingType] = useState<CategoryTreeNode['categoryType'] | null>(null)
  const [saving, setSaving] = useState(false)
  const treeRef = useRef<any>(null)

  const loadForests = useCallback(async () => {
    try {
      const res = await jingqueAdminApi.categoryForests.list()
      setForests(res.forests || [])
      if (res.forests?.length && !selectedForestId) {
        setSelectedForestId(res.forests[0].id)
      }
    } catch (e: any) { setError(e.message) }
  }, [selectedForestId])

  const loadTree = useCallback(async () => {
    if (!selectedForestId) return
    setLoading(true)
    setError('')
    try {
      const res = await jingqueAdminApi.categoryTree.get(selectedForestId)
      setTreeData(convertToTree(res.tree || []))
    } catch (e: any) { setError(e.message) }
    finally { setLoading(false) }
  }, [selectedForestId])

  useEffect(() => { loadForests() }, [])
  useEffect(() => { loadTree() }, [loadTree])

  const handleCreateForest = async (data: { code: string; name: string; description: string }) => {
    setSaving(true)
    try {
      await jingqueAdminApi.categoryForests.create(data)
      setShowCreateForest(false)
      loadForests()
    } catch (e: any) { alert(e.message) }
    finally { setSaving(false) }
  }

  const handleCreateNode = async (data: { name: string; value?: string; description?: string; extractionPrompt?: string }) => {
    if (!showCreateNode || !selectedForestId) return
    setSaving(true)
    try {
      if (showCreateNode.type === 'folder') {
        await jingqueAdminApi.categoryTree.createFolder({ forestId: selectedForestId, parentId: showCreateNode.parentId || undefined, name: data.name })
      } else {
        await jingqueAdminApi.categoryTree.createLeaf({ forestId: selectedForestId, parentId: showCreateNode.parentId || undefined, name: data.name, value: data.value, description: data.description, extractionPrompt: data.extractionPrompt, enabled: true })
      }
      setShowCreateNode(null)
      loadTree()
    } catch (e: any) { alert(e.message) }
    finally { setSaving(false) }
  }

  const handleUpdateNode = async (id: string, data: { name?: string; enabled?: boolean }) => {
    setSaving(true)
    try {
      await jingqueAdminApi.categoryTree.update(id, data)
      setEditingNode(null)
      loadTree()
    } catch (e: any) { alert(e.message) }
    finally { setSaving(false) }
  }

  const handleDeleteNode = async (id: string) => {
    if (!confirm('确认删除？')) return
    try {
      await jingqueAdminApi.categoryTree.delete(id)
      loadTree()
    } catch (e: any) { alert(e.message) }
  }

  const handleUpdateType = async (id: string, data: UpdateCategoryTypeRequest) => {
    setSaving(true)
    try {
      await jingqueAdminApi.categoryTypes.update(id, data)
      setEditingType(null)
      loadTree()
    } catch (e: any) { alert(e.message) }
    finally { setSaving(false) }
  }

  const handleContextMenu = (e: React.MouseEvent, node: NodeApi<any>) => {
    e.preventDefault()
    setContextMenu({ x: e.clientX, y: e.clientY, node })
  }

  const closeContextMenu = () => setContextMenu(null)

  const mindmapData = treeData

  return (
    <div className="p-8 flex flex-col h-[calc(100vh-64px)]">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-800">分类树</h1>
        <div className="flex items-center gap-3">
          <select
            value={selectedForestId}
            onChange={e => setSelectedForestId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">请选择 Forest</option>
            {forests.map(f => <option key={f.id} value={f.id}>{f.name} ({f.code})</option>)}
          </select>
          <button onClick={() => setShowCreateForest(true)} className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">新建Forest</button>
          <button onClick={loadTree} disabled={!selectedForestId} className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50">刷新</button>
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button onClick={() => setViewMode('tree')} className={`px-3 py-1.5 text-sm ${viewMode === 'tree' ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>🌲 列表</button>
            <button onClick={() => setViewMode('mindmap')} className={`px-3 py-1.5 text-sm ${viewMode === 'mindmap' ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>🧠 脑图</button>
          </div>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}

      {!selectedForestId ? (
        <div className="flex-1 flex items-center justify-center text-gray-400">请选择或创建一个 Forest</div>
      ) : loading ? (
        <div className="flex-1 flex items-center justify-center">
          <svg className="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
        </div>
      ) : viewMode === 'tree' ? (
        <div className="flex-1 overflow-auto bg-white border border-gray-200 rounded-xl">
          <Tree
            ref={treeRef}
            data={treeData}
            onContextMenu={handleContextMenu as any}
            openByDefault={false}
            width="100%"
            height={800}
          >
            {({ node, style, dragHandle }: any) => (
              <div
                ref={dragHandle as any}
                style={style}
                className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 rounded text-sm ${!node.data?.enabled ? 'opacity-50' : ''}`}
                onContextMenu={(e) => handleContextMenu(e, node)}
              >
                <span className="text-base">{node.data?.nodeType === 'folder' ? (node.isOpen ? '📂' : '📁') : '📄'}</span>
                <span className={node.data?.nodeType === 'leaf' ? 'text-green-700' : 'text-blue-700'}>{node.data?.name}</span>
                {node.data?.categoryType && (
                  <span className="ml-auto text-xs text-gray-400">{node.data.categoryType.name}</span>
                )}
              </div>
            )}
          </Tree>
        </div>
      ) : (
        <div className="flex-1 bg-white border border-gray-200 rounded-xl overflow-hidden">
          <CategoryMindMap data={mindmapData as any} />
        </div>
      )}

      {contextMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={closeContextMenu} />
          <div
            className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px]"
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            {contextMenu.node.data.nodeType === 'folder' ? (
              <>
                <MenuItem label="📁 新建子文件夹" onClick={() => { setShowCreateNode({ parentId: contextMenu.node.id, type: 'folder' }); closeContextMenu() }} />
                <MenuItem label="📄 新建子叶子" onClick={() => { setShowCreateNode({ parentId: contextMenu.node.id, type: 'leaf' }); closeContextMenu() }} />
                <MenuItem label="✏️ 重命名" onClick={() => { setEditingNode({ id: contextMenu.node.id, name: contextMenu.node.data.name, enabled: contextMenu.node.data.enabled }); closeContextMenu() }} />
                <MenuItem label={contextMenu.node.data.enabled ? '⊘ 禁用' : '✓ 启用'} onClick={async () => { await handleUpdateNode(contextMenu.node.id, { enabled: !contextMenu.node.data.enabled }); closeContextMenu() }} />
                <MenuItem label="🗑️ 删除" onClick={async () => { await handleDeleteNode(contextMenu.node.id); closeContextMenu() }} isDanger />
              </>
            ) : (
              <>
                <MenuItem label="✏️ 编辑分类类型" onClick={() => { setEditingType(contextMenu.node.data.categoryType || { id: '', name: contextMenu.node.data.name, description: '', extractionPrompt: '', enabled: true }); closeContextMenu() }} />
                <MenuItem label={contextMenu.node.data.enabled ? '⊘ 禁用' : '✓ 启用'} onClick={async () => { await handleUpdateNode(contextMenu.node.id, { enabled: !contextMenu.node.data.enabled }); closeContextMenu() }} />
                <MenuItem label="🗑️ 删除" onClick={async () => { await handleDeleteNode(contextMenu.node.id); closeContextMenu() }} isDanger />
              </>
            )}
          </div>
        </>
      )}

      {showCreateForest && (
        <ForestModal saving={saving} onSave={handleCreateForest} onClose={() => setShowCreateForest(false)} />
      )}

      {showCreateNode && (
        <NodeModal type={showCreateNode.type} saving={saving} onSave={handleCreateNode} onClose={() => setShowCreateNode(null)} />
      )}

      {editingNode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl w-full max-w-sm">
            <div className="p-4 border-b"><h2 className="font-medium">重命名</h2></div>
            <div className="p-4 space-y-3">
              <input value={editingNode.name} onChange={e => setEditingNode(n => n ? { ...n, name: e.target.value } : null)} className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <button onClick={() => setEditingNode(null)} className="px-4 py-2 text-sm bg-gray-100 rounded-lg">取消</button>
              <button onClick={() => handleUpdateNode(editingNode.id, { name: editingNode.name })} disabled={saving} className="px-4 py-2 text-sm bg-primary text-white rounded-lg disabled:opacity-50">保存</button>
            </div>
          </div>
        </div>
      )}

      {editingType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-4 border-b flex justify-between"><h2 className="font-medium">编辑分类类型</h2><button onClick={() => setEditingType(null)} className="text-gray-400">✕</button></div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">名称</label>
                <input value={editingType.name} onChange={e => setEditingType(t => t ? { ...t, name: e.target.value } : null)} className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
                <textarea value={editingType.description} onChange={e => setEditingType(t => t ? { ...t, description: e.target.value } : null)} className="w-full px-3 py-2 border rounded-lg text-sm" rows={2} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">提取Prompt</label>
                <textarea value={editingType.extractionPrompt} onChange={e => setEditingType(t => t ? { ...t, extractionPrompt: e.target.value } : null)} className="w-full px-3 py-2 border rounded-lg text-sm" rows={3} />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <button onClick={() => setEditingType(null)} className="px-4 py-2 text-sm bg-gray-100 rounded-lg">取消</button>
              <button onClick={() => handleUpdateType(editingType.id, { name: editingType.name, description: editingType.description, extractionPrompt: editingType.extractionPrompt })} disabled={saving} className="px-4 py-2 text-sm bg-primary text-white rounded-lg disabled:opacity-50">保存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const MenuItem: FC<{ label: string; onClick: () => void; isDanger?: boolean }> = ({ label, onClick, isDanger }) => (
  <button onClick={onClick} className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${isDanger ? 'text-red-500' : 'text-gray-700'}`}>{label}</button>
)

const ForestModal: FC<{ saving: boolean; onSave: (d: any) => void; onClose: () => void }> = ({ saving, onSave, onClose }) => {
  const [form, setForm] = useState({ code: '', name: '', description: '' })
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl w-full max-w-sm">
        <div className="p-4 border-b flex justify-between"><h2 className="font-medium">新建Forest</h2><button onClick={onClose} className="text-gray-400">✕</button></div>
        <div className="p-4 space-y-3">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Code</label><input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">名称</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">描述</label><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" rows={2} /></div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-gray-100 rounded-lg">取消</button>
          <button onClick={() => onSave(form)} disabled={saving || !form.code || !form.name} className="px-4 py-2 text-sm bg-primary text-white rounded-lg disabled:opacity-50">{saving ? '保存中...' : '保存'}</button>
        </div>
      </div>
    </div>
  )
}

const NodeModal: FC<{ type: 'folder' | 'leaf'; saving: boolean; onSave: (d: any) => void; onClose: () => void }> = ({ type, saving, onSave, onClose }) => {
  const [form, setForm] = useState({ name: '', value: '', description: '', extractionPrompt: '' })
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl w-full max-w-md">
        <div className="p-4 border-b flex justify-between"><h2 className="font-medium">{type === 'folder' ? '新建文件夹' : '新建叶子节点'}</h2><button onClick={onClose} className="text-gray-400">✕</button></div>
        <div className="p-4 space-y-3">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">名称</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
          {type === 'leaf' && (
            <>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">标识(可选)</label><input value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">描述</label><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" rows={2} /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">提取Prompt</label><textarea value={form.extractionPrompt} onChange={e => setForm(f => ({ ...f, extractionPrompt: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" rows={3} /></div>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-gray-100 rounded-lg">取消</button>
          <button onClick={() => onSave(form)} disabled={saving || !form.name} className="px-4 py-2 text-sm bg-primary text-white rounded-lg disabled:opacity-50">{saving ? '保存中...' : '保存'}</button>
        </div>
      </div>
    </div>
  )
}
